import React, { useEffect, useState } from "react";
import {
  User,
  Users,
  Search,
  Trash2,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Calendar,
  Shield,
  Eye,
  Download,
  Globe,
  MapPin,
  Briefcase,
  Award,
  Activity,
  BarChart,
  Database,
  FileText,
  BookOpen,
  CheckCircle,
  Key,
  Save,
  Copy,
  Check,
  ExternalLink,
  Send,
  Sliders,
  Terminal,
  Layers,
  GitBranch,
  Rss,
  Edit3
} from "react-feather";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  setDoc,
  query,
  where
} from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { db, getAllUsers, auth, restoreUser } from "shared/utils/firebase";
import PageHeader from "shared/components/PageHeader/PageHeader";
import styles from "./UserManagement.module.css";

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDeletedStatus, setFilterDeletedStatus] = useState("active");

  // Modal States
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalTab, setModalTab] = useState("overview");
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  // User Detailed Sub-Collections State
  const [userDetailedData, setUserDetailedData] = useState({
    enrolledCourses: [],
    readModules: [],
    testResults: [],
    projects: [],
    events: [],
    certificates: [],
    codePracticeCount: 0,
    blogsCount: 0,
    notesCount: 0,
    pointsData: { totalPoints: 0, rank: "Silicon Initiate", breakdown: {} },
    auditLogs: []
  });

  // Admin Settings Control Form State inside Modal
  const [adminFormState, setAdminFormState] = useState({
    role: "Student",
    deactivated: false,
    deactivationReason: "",
    customBadge: "",
    bonusPoints: "",
    bonusReason: "",
    forcePasswordReset: false,
    emailVerified: false,
    phoneVerified: false,
    adminNotes: ""
  });
  const [isSavingAdmin, setIsSavingAdmin] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isSendingEmailVerify, setIsSendingEmailVerify] = useState(false);
  const [isSendingMobileVerify, setIsSendingMobileVerify] = useState(false);
  const [adminMsg, setAdminMsg] = useState({ type: "", text: "" });
  const [copiedUid, setCopiedUid] = useState(false);

  // Delete & Restore Action Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [userToRestore, setUserToRestore] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [filterDeletedStatus]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, filterRole, filterStatus]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersSnapshot = await getAllUsers(filterDeletedStatus);

      const [testResultsSnapshot, projectsSnapshot] = await Promise.all([
        getDocs(collection(db, "testResults")),
        getDocs(collection(db, "projects"))
      ]);

      const usersData = usersSnapshot.docs.map(docSnap => {
        const userData = docSnap.data();
        const userId = docSnap.id;

        const userTestResults = testResultsSnapshot.docs.filter(
          testDoc => testDoc.data().userId === userId
        );
        const userProjects = projectsSnapshot.docs.filter(
          projectDoc => projectDoc.data().refUser === userId || projectDoc.data().userId === userId
        );

        const totalTests = userTestResults.length;
        const totalProjects = userProjects.length;
        const activityScore = (totalTests * 2) + (totalProjects * 3) + (userData.lastLogin ? 1 : 0);

        const lastTestDate = userTestResults.length > 0
          ? userTestResults
            .sort((a, b) => (b.data().createdAt?.toDate() || 0) - (a.data().createdAt?.toDate() || 0))[0]
            .data().createdAt?.toDate()
          : null;

        return {
          id: userId,
          ...userData,
          createdAt: userData.createdAt?.toDate(),
          lastLogin: userData.lastLogin?.toDate(),
          lastTestDate: lastTestDate,
          totalTests: totalTests,
          totalProjects: totalProjects,
          activityScore: activityScore,
          username: userData.username || userData.email?.split('@')[0],
          profileImage: userData.profileImage || userData.photoURL,
          bio: userData.bio || userData.about,
          city: userData.city || "",
          state: userData.state || "",
          location: userData.location || userData.address || (userData.city ? `${userData.city}${userData.state ? `, ${userData.state}` : ''}` : ''),
          designation: userData.designation || userData.jobTitle || userData.stream,
          department: userData.department || userData.company || userData.institute,
          linkedin: userData.linkedin || userData.linkedIn,
          github: userData.github || userData.gitHub,
          website: userData.website || userData.personalWebsite,
          skills: userData.skills || [],
          interests: userData.interests || [],
          education: userData.education || userData.qualification,
          emailVerified: !!(userData.emailVerified || userData.isEmailVerified),
          phoneVerified: !!(userData.phoneVerified || userData.isPhoneVerified || userData.mobileVerified)
        };
      });

      const currentAdminId = auth.currentUser?.uid;
      const filteredUsersData = usersData.filter(user => user.id !== currentAdminId);

      setUsers(filteredUsersData);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.username?.toLowerCase().includes(term) ||
        user.phone?.toLowerCase().includes(term) ||
        user.city?.toLowerCase().includes(term) ||
        user.state?.toLowerCase().includes(term) ||
        user.designation?.toLowerCase().includes(term) ||
        user.department?.toLowerCase().includes(term) ||
        user.location?.toLowerCase().includes(term) ||
        user.education?.toLowerCase().includes(term)
      );
    }

    if (filterRole !== "all") {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    if (filterStatus !== "all") {
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      if (filterStatus === "active") {
        filtered = filtered.filter(user =>
          user.lastLogin && user.lastLogin >= sevenDaysAgo && !user.deactivated
        );
      } else if (filterStatus === "inactive") {
        filtered = filtered.filter(user =>
          !user.lastLogin || user.lastLogin < sevenDaysAgo || user.deactivated
        );
      }
    }

    setFilteredUsers(filtered);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const userRef = doc(db, "users", userId);
      const updates = { role: newRole, isAdmin: newRole === 'Admin', updatedAt: new Date() };
      await updateDoc(userRef, updates);

      setUsers(users.map(user =>
        user.id === userId ? { ...user, ...updates } : user
      ));

      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({ ...selectedUser, ...updates });
        setAdminFormState(prev => ({ ...prev, role: newRole }));
      }
    } catch (error) {
      console.error("Error updating user role:", error);
    }
  };

  const handleSuspendUser = async (userId, shouldSuspend) => {
    try {
      const userRef = doc(db, "users", userId);
      const currentUserId = auth.currentUser?.uid;

      const updates = shouldSuspend
        ? {
          deactivated: true,
          deactivatedAt: new Date(),
          deactivatedBy: currentUserId,
          deactivationReason: "Account suspended by administrator"
        }
        : {
          deactivated: false,
          reactivatedAt: new Date(),
          reactivatedBy: currentUserId,
          deactivatedAt: null,
          deactivatedBy: null,
          deactivationReason: null
        };

      await updateDoc(userRef, updates);

      await addDoc(collection(db, "adminActions"), {
        action: shouldSuspend ? "USER_SUSPENDED" : "USER_REACTIVATED",
        targetUserId: userId,
        performedBy: currentUserId,
        performedAt: new Date()
      });

      fetchUsers();
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser(prev => ({ ...prev, ...updates }));
        setAdminFormState(prev => ({ ...prev, deactivated: shouldSuspend }));
      }

      alert(shouldSuspend ? "User account suspended successfully!" : "User account reactivated successfully!");
    } catch (error) {
      console.error("Error updating suspension status:", error);
      alert("Failed to update user status.");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const currentUserId = auth.currentUser?.uid;
      if (userToDelete.id === currentUserId) {
        alert("You cannot delete your own account!");
        return;
      }

      const adminUsers = users.filter(u => u.role === 'Admin' && u.id !== userToDelete.id);
      if (userToDelete.role === 'Admin' && adminUsers.length === 0) {
        alert("Cannot delete the last admin account!");
        return;
      }

      const userRef = doc(db, "users", userToDelete.id);
      await updateDoc(userRef, {
        deleted: true,
        deletedAt: new Date(),
        deletedBy: currentUserId,
        isActive: false
      });

      await addDoc(collection(db, "adminActions"), {
        action: "USER_DELETED",
        targetUserId: userToDelete.id,
        targetUserEmail: userToDelete.email,
        targetUserName: userToDelete.name || userToDelete.email,
        targetUserRole: userToDelete.role,
        performedBy: currentUserId,
        performedAt: new Date()
      });

      setUsers(users.filter(user => user.id !== userToDelete.id));
      setShowDeleteModal(false);
      setUserToDelete(null);

      alert("User account deactivated successfully!");
    } catch (error) {
      console.error("Error deactivating user:", error);
      alert("Failed to deactivate user. Please try again.");
    }
  };

  const handleRestoreUser = async () => {
    if (!userToRestore) return;
    try {
      await restoreUser(userToRestore.id);
      const currentUserId = auth.currentUser?.uid;
      await addDoc(collection(db, "adminActions"), {
        action: "USER_RESTORED",
        targetUserId: userToRestore.id,
        targetUserEmail: userToRestore.email,
        targetUserName: userToRestore.name || userToRestore.email,
        targetUserRole: userToRestore.role,
        performedBy: currentUserId,
        performedAt: new Date()
      });

      setUsers(users.filter(user => user.id !== userToRestore.id));
      setShowRestoreModal(false);
      setUserToRestore(null);

      alert("User account restored successfully!");
    } catch (error) {
      console.error("Error restoring user:", error);
      alert("Failed to restore user. Please try again.");
    }
  };

  // Exact User-Side Silicon Points Calculation Logic (Points awarded only for Attended/Completed Events)
  const calculateUserSiliconPoints = async (userId, { enrolledCourses = [], readModules = [], testResults = [], projects = [], events = [], codePracticeCount = 0 }) => {
    try {
      let pointsFromCourses = 0;
      let pointsFromModules = 0;
      let pointsFromProjects = 0;
      let pointsFromTests = 0;
      let pointsFromEvents = 0;
      let pointsFromCode = codePracticeCount * 50;

      // 1. Courses Points (proportional progress to base points)
      enrolledCourses.forEach(c => {
        const pct = Math.min(100, Math.max(0, Number(c.progress || 0)));
        const base = Number(c.siliconPoints !== undefined ? c.siliconPoints : 50);
        pointsFromCourses += Math.round((pct / 100) * base);
      });

      // 2. Reading Modules Points (proportional completion percentage)
      readModules.forEach(m => {
        const pct = Math.min(100, Math.max(0, Number(m.percentage || 0)));
        const base = Number(m.siliconPoints !== undefined ? m.siliconPoints : 50);
        pointsFromModules += Math.round((pct / 100) * base);
      });

      // 3. Lab Projects Points
      projects.forEach(p => {
        const pct = p.status === "completed" ? 100 : (p.percentage || 50);
        const base = Number(p.siliconPoints !== undefined ? p.siliconPoints : 50);
        pointsFromProjects += Math.round((pct / 100) * base);
      });

      // 4. MCQ Tests Points
      testResults.forEach(t => {
        const score = Math.min(100, Math.max(0, Number(t.score || 0)));
        const base = Number(t.siliconPoints !== undefined ? t.siliconPoints : 50);
        pointsFromTests += Math.round((score / 100) * base);
      });

      // 5. Attended / Completed Events Points (Only awarded for attended/completed events)
      let attendedEventsCount = 0;
      events.forEach(e => {
        const isAttended = e.status === "attended" || e.status === "completed" || e.status === "present" || e.attended === true || e.completed === true;
        if (isAttended) {
          attendedEventsCount++;
          const base = Number(e.siliconPoints !== undefined ? e.siliconPoints : 50);
          pointsFromEvents += base;
        }
      });

      // Check stored userPoints document in Firestore for stored totalPoints or admin manual adjustments
      let storedTotal = null;
      let storedRank = null;
      try {
        const pSnap = await getDoc(doc(db, "userPoints", userId));
        if (pSnap.exists()) {
          const pData = pSnap.data();
          if (pData.totalPoints !== undefined) storedTotal = pData.totalPoints;
          if (pData.rank) storedRank = pData.rank;
        }
      } catch (e) {
        console.warn("userPoints doc fetch notice:", e);
      }

      const calculatedTotal = pointsFromCourses + pointsFromModules + pointsFromProjects + pointsFromTests + pointsFromEvents + pointsFromCode;
      const totalPoints = storedTotal !== null ? storedTotal : calculatedTotal;

      let rank = storedRank || "Silicon Initiate";
      if (!storedRank) {
        if (totalPoints >= 1000) rank = "Silicon Legend";
        else if (totalPoints >= 500) rank = "VLSI Master";
        else if (totalPoints >= 250) rank = "Verilog Craftsman";
        else if (totalPoints >= 100) rank = "Silicon Scholar";
      }

      return {
        totalPoints,
        rank,
        breakdown: {
          courses: { points: pointsFromCourses, count: enrolledCourses.length },
          modules: { points: pointsFromModules, count: readModules.length },
          projects: { points: pointsFromProjects, count: projects.length },
          tests: { points: pointsFromTests, count: testResults.length },
          events: { points: pointsFromEvents, count: attendedEventsCount },
          codePractice: { points: pointsFromCode, count: codePracticeCount }
        }
      };
    } catch (err) {
      console.error("Error in calculateUserSiliconPoints:", err);
      return { totalPoints: 0, rank: "Silicon Initiate", breakdown: {} };
    }
  };

  // Comprehensive Real User Sub-Collection Data Fetching for Modal
  const openUserModal = async (user) => {
    setSelectedUser(user);
    setModalTab("overview");
    setShowUserModal(true);
    setIsDetailLoading(true);
    setAdminMsg({ type: "", text: "" });

    setAdminFormState({
      role: user.role || "Student",
      deactivated: !!user.deactivated,
      deactivationReason: user.deactivationReason || "",
      customBadge: user.customBadge || "",
      bonusPoints: "",
      bonusReason: "",
      forcePasswordReset: !!user.forcePasswordReset,
      emailVerified: !!user.emailVerified,
      phoneVerified: !!user.phoneVerified,
      adminNotes: user.adminNotes || ""
    });

    try {
      const userId = user.id;

      // 1. Fetch Enrolled Courses with Fallback & Course Details Lookup
      let courseEnrollmentsSnap = await getDocs(query(collection(db, "courseEnrollments"), where("userId", "==", userId)));
      if (courseEnrollmentsSnap.empty) {
        courseEnrollmentsSnap = await getDocs(query(collection(db, "enrollments"), where("userId", "==", userId)));
      }

      const enrolledCourses = await Promise.all(
        courseEnrollmentsSnap.docs.map(async (enrollDoc) => {
          const data = enrollDoc.data();
          const courseId = data.courseId || enrollDoc.id;
          let courseData = { title: data.courseName || data.title || `Course ID: ${courseId}` };

          try {
            let cDoc = await getDoc(doc(db, "courseList", courseId));
            if (!cDoc.exists()) {
              cDoc = await getDoc(doc(db, "courses", courseId));
            }
            if (cDoc.exists()) {
              courseData = cDoc.data();
            }
          } catch (err) {
            console.warn("Error fetching course detail for", courseId, err);
          }

          return {
            id: courseId,
            enrollmentId: enrollDoc.id,
            title: courseData.title || courseData.courseName || "Untitled Course",
            category: courseData.category || "VLSI & Embedded",
            progress: data.progress || 0,
            status: data.status || (data.progress >= 100 ? "completed" : "active"),
            completedLessons: data.completedLessons?.length || data.completedLessonsCount || 0,
            totalLessons: courseData.lessons?.length || courseData.totalLessons || 0,
            enrolledAt: data.enrolledAt?.toDate ? data.enrolledAt.toDate() : (data.createdAt?.toDate ? data.createdAt.toDate() : null),
            lastAccessedAt: data.lastAccessedAt?.toDate ? data.lastAccessedAt.toDate() : null,
            siliconPoints: courseData.siliconPoints
          };
        })
      );

      // 2. Fetch Read Modules Progress (readingUserProgress / userModuleProgress / readModuleProgress)
      let readModulesSnap = await getDocs(query(collection(db, "readingUserProgress"), where("userId", "==", userId)));
      if (readModulesSnap.empty) {
        readModulesSnap = await getDocs(query(collection(db, "userModuleProgress"), where("userId", "==", userId)));
      }
      if (readModulesSnap.empty) {
        readModulesSnap = await getDocs(query(collection(db, "readModuleProgress"), where("userId", "==", userId)));
      }

      const readModules = await Promise.all(
        readModulesSnap.docs.map(async (mDoc) => {
          const data = mDoc.data();
          const moduleId = data.moduleId || mDoc.id;
          let moduleTitle = data.title || data.moduleTitle || "Read Module";
          let modulePoints = undefined;

          try {
            let modSnap = await getDoc(doc(db, "readingModules", moduleId));
            if (!modSnap.exists()) {
              modSnap = await getDoc(doc(db, "textModules", moduleId));
            }
            if (modSnap.exists()) {
              moduleTitle = modSnap.data().title || moduleTitle;
              modulePoints = modSnap.data().siliconPoints;
            }
          } catch (err) {
            console.warn("Error fetching reading module detail for", moduleId, err);
          }

          return {
            id: moduleId,
            progressId: mDoc.id,
            title: moduleTitle,
            percentage: data.completionPercentage || data.percentage || (data.completed ? 100 : 0),
            completedTopics: data.completedTopics?.length || 0,
            lastUpdated: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.lastAccessedAt?.toDate ? data.lastAccessedAt.toDate() : null),
            siliconPoints: modulePoints
          };
        })
      );

      // 3. Fetch Test Evaluation Results (testResults / practiceTestResults)
      let testResultsSnap = await getDocs(query(collection(db, "testResults"), where("userId", "==", userId)));
      if (testResultsSnap.empty) {
        testResultsSnap = await getDocs(query(collection(db, "practiceTestResults"), where("userId", "==", userId)));
      }
      const testResults = testResultsSnap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          testTitle: data.testTitle || data.title || "MCQ Evaluation Test",
          score: data.score || data.percentage || 0,
          passed: data.passed || data.isPassed || (data.score >= 50),
          totalQuestions: data.totalQuestions || 0,
          correctAnswers: data.correctAnswers || 0,
          takenAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
          siliconPoints: data.siliconPoints
        };
      });

      // 4. Fetch Lab Projects (projects / registeredProjects)
      let projectsSnap = await getDocs(query(collection(db, "projects"), where("refUser", "==", userId)));
      if (projectsSnap.empty) {
        projectsSnap = await getDocs(query(collection(db, "projects"), where("userId", "==", userId)));
      }
      if (projectsSnap.empty) {
        projectsSnap = await getDocs(query(collection(db, "registeredProjects"), where("userId", "==", userId)));
      }
      const projects = projectsSnap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          title: data.title || data.projectTitle || "Lab Project Submission",
          status: data.status || "submitted",
          submittedAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.registeredAt?.toDate ? data.registeredAt.toDate() : null),
          siliconPoints: data.siliconPoints
        };
      });

      // 5. Fetch Events Registrations (query eventRegistrations & registeredEvents)
      let eventsSnap = await getDocs(query(collection(db, "eventRegistrations"), where("userId", "==", userId)));
      if (eventsSnap.empty) {
        eventsSnap = await getDocs(query(collection(db, "registeredEvents"), where("userId", "==", userId)));
      }
      const events = eventsSnap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ticketCode: data.ticketCode || "PASS-LOGIX",
          eventTitle: data.eventTitle || "SiliconLogix Event",
          eventCategory: data.eventCategory || "Workshop",
          status: data.status || "registered",
          attended: data.attended || data.status === "attended" || data.status === "completed" || data.status === "present",
          registeredAt: data.registeredAt?.toDate ? data.registeredAt.toDate() : null,
          siliconPoints: data.siliconPoints
        };
      });

      // 6. Fetch Certificates
      const certificatesSnap = await getDocs(query(collection(db, "certificates"), where("userId", "==", userId)));
      const certificates = certificatesSnap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          certificateId: data.certificateId || d.id,
          title: data.title || data.courseTitle || "Certificate of Completion",
          issuedAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.issuedAt?.toDate ? data.issuedAt.toDate() : null)
        };
      });

      // 7. Fetch Code Practice Submissions Count
      let codeSnap = await getDocs(query(collection(db, "userCodeProgress"), where("userId", "==", userId)));
      if (codeSnap.empty) {
        codeSnap = await getDocs(query(collection(db, "codeProgress"), where("userId", "==", userId)));
      }
      const codePracticeCount = codeSnap.size;

      // 8. Fetch Blogs Posted Count
      let blogsSnap = await getDocs(query(collection(db, "blogs"), where("userId", "==", userId)));
      if (blogsSnap.empty) {
        blogsSnap = await getDocs(query(collection(db, "userBlogs"), where("userId", "==", userId)));
      }
      const blogsCount = blogsSnap.size;

      // 9. Fetch Notes Added Count
      let notesSnap = await getDocs(query(collection(db, "userNotes"), where("userId", "==", userId)));
      if (notesSnap.empty) {
        notesSnap = await getDocs(query(collection(db, "notes"), where("userId", "==", userId)));
      }
      const notesCount = notesSnap.size;

      // 10. Calculate Silicon Points using User-Side Exact Logic
      const pointsData = await calculateUserSiliconPoints(userId, {
        enrolledCourses,
        readModules,
        testResults,
        projects,
        events,
        codePracticeCount
      });

      // 11. Fetch Audit Logs
      const adminActionsSnap = await getDocs(query(collection(db, "adminActions"), where("targetUserId", "==", userId)));
      const auditLogs = adminActionsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.performedAt?.toDate() || 0) - (a.performedAt?.toDate() || 0));

      setUserDetailedData({
        enrolledCourses,
        readModules,
        testResults,
        projects,
        events,
        certificates,
        codePracticeCount,
        blogsCount,
        notesCount,
        pointsData,
        auditLogs
      });
    } catch (err) {
      console.error("Error fetching detailed user profile data:", err);
    } finally {
      setIsDetailLoading(false);
    }
  };

  // Save Admin Control Settings Form inside Modal
  const handleSaveAdminSettings = async () => {
    if (!selectedUser) return;
    setIsSavingAdmin(true);
    setAdminMsg({ type: "", text: "" });

    try {
      const currentUserId = auth.currentUser?.uid;
      const userRef = doc(db, "users", selectedUser.id);

      const userUpdates = {
        role: adminFormState.role,
        isAdmin: adminFormState.role === "Admin",
        deactivated: adminFormState.deactivated,
        deactivationReason: adminFormState.deactivated ? adminFormState.deactivationReason : null,
        customBadge: adminFormState.customBadge || null,
        forcePasswordReset: adminFormState.forcePasswordReset || false,
        emailVerified: adminFormState.emailVerified,
        phoneVerified: adminFormState.phoneVerified,
        adminNotes: adminFormState.adminNotes || "",
        updatedAt: new Date(),
        updatedBy: currentUserId
      };

      if (adminFormState.deactivated && !selectedUser.deactivated) {
        userUpdates.deactivatedAt = new Date();
        userUpdates.deactivatedBy = currentUserId;
      } else if (!adminFormState.deactivated && selectedUser.deactivated) {
        userUpdates.deactivatedAt = null;
        userUpdates.deactivatedBy = null;
        userUpdates.reactivatedAt = new Date();
        userUpdates.reactivatedBy = currentUserId;
      }

      await updateDoc(userRef, userUpdates);

      if (adminFormState.bonusPoints && !isNaN(Number(adminFormState.bonusPoints))) {
        const bonusToAdd = Number(adminFormState.bonusPoints);
        const pointsRef = doc(db, "userPoints", selectedUser.id);
        const currentPoints = userDetailedData.pointsData?.totalPoints || 0;
        const newTotal = currentPoints + bonusToAdd;

        await setDoc(pointsRef, {
          totalPoints: newTotal,
          lastAdjustedAt: new Date(),
          lastAdjustedBy: currentUserId,
          lastAdjustmentReason: adminFormState.bonusReason || "Admin Manual Adjustment"
        }, { merge: true });

        setUserDetailedData(prev => ({
          ...prev,
          pointsData: { ...prev.pointsData, totalPoints: newTotal }
        }));
      }

      await addDoc(collection(db, "adminActions"), {
        action: "USER_ADMIN_UPDATE",
        targetUserId: selectedUser.id,
        targetUserEmail: selectedUser.email,
        updatesApplied: {
          role: adminFormState.role,
          deactivated: adminFormState.deactivated,
          customBadge: adminFormState.customBadge,
          emailVerified: adminFormState.emailVerified,
          phoneVerified: adminFormState.phoneVerified,
          bonusPoints: adminFormState.bonusPoints ? Number(adminFormState.bonusPoints) : 0
        },
        performedBy: currentUserId,
        performedAt: new Date()
      });

      const updatedUserObj = { ...selectedUser, ...userUpdates };
      setSelectedUser(updatedUserObj);
      setUsers(users.map(u => u.id === selectedUser.id ? updatedUserObj : u));

      setAdminMsg({ type: "success", text: "Admin settings & user control configurations saved successfully!" });
    } catch (error) {
      console.error("Error updating admin settings:", error);
      setAdminMsg({ type: "danger", text: "Failed to save admin settings: " + error.message });
    } finally {
      setIsSavingAdmin(false);
    }
  };

  // Send Password Reset Email Trigger
  const handleSendPasswordResetModal = async () => {
    if (!selectedUser?.email) return;
    try {
      setIsSendingReset(true);
      await sendPasswordResetEmail(auth, selectedUser.email);

      await addDoc(collection(db, "adminActions"), {
        action: "ADMIN_PASSWORD_RESET_SENT",
        targetUserId: selectedUser.id,
        targetUserEmail: selectedUser.email,
        performedBy: auth.currentUser?.uid,
        performedAt: new Date()
      });

      setAdminMsg({ type: "success", text: `Password reset email dispatched to ${selectedUser.email}!` });
    } catch (err) {
      console.error("Password reset error:", err);
      setAdminMsg({ type: "danger", text: "Password reset error: " + err.message });
    } finally {
      setIsSendingReset(false);
    }
  };

  // Send Email Verification Request
  const handleSendEmailVerificationReq = async () => {
    if (!selectedUser?.email) return;
    try {
      setIsSendingEmailVerify(true);
      await addDoc(collection(db, "adminActions"), {
        action: "ADMIN_EMAIL_VERIFICATION_DISPATCHED",
        targetUserId: selectedUser.id,
        targetUserEmail: selectedUser.email,
        performedBy: auth.currentUser?.uid,
        performedAt: new Date()
      });
      await updateDoc(doc(db, "users", selectedUser.id), {
        verificationEmailSentAt: new Date(),
        updatedAt: new Date()
      });
      setAdminMsg({ type: "success", text: `Verification link request dispatched to email: ${selectedUser.email}` });
    } catch (err) {
      console.error("Email verification dispatch error:", err);
      setAdminMsg({ type: "danger", text: "Failed to dispatch email verification: " + err.message });
    } finally {
      setIsSendingEmailVerify(false);
    }
  };

  // Send Mobile / SMS OTP Verification Request
  const handleSendMobileVerificationReq = async () => {
    if (!selectedUser?.phone) {
      alert("User has no registered mobile phone number!");
      return;
    }
    try {
      setIsSendingMobileVerify(true);
      await addDoc(collection(db, "adminActions"), {
        action: "ADMIN_SMS_VERIFICATION_DISPATCHED",
        targetUserId: selectedUser.id,
        targetUserPhone: selectedUser.phone,
        performedBy: auth.currentUser?.uid,
        performedAt: new Date()
      });
      await updateDoc(doc(db, "users", selectedUser.id), {
        verificationSmsSentAt: new Date(),
        updatedAt: new Date()
      });
      setAdminMsg({ type: "success", text: `SMS verification OTP request dispatched to mobile: ${selectedUser.phone}` });
    } catch (err) {
      console.error("Mobile verification dispatch error:", err);
      setAdminMsg({ type: "danger", text: "Failed to dispatch SMS verification: " + err.message });
    } finally {
      setIsSendingMobileVerify(false);
    }
  };

  const handleCopyUid = (uid) => {
    navigator.clipboard.writeText(uid);
    setCopiedUid(true);
    setTimeout(() => setCopiedUid(false), 2000);
  };

  const exportUsers = () => {
    const csvContent = [
      ["Username", "Name", "Email", "Email Verified", "Phone", "Mobile Verified", "Role", "Status", "Joined Date", "Last Login"].join(","),
      ...filteredUsers.map(user => [
        `"${user.username || user.email?.split('@')[0]}"`,
        `"${user.name || user.email}"`,
        `"${user.email}"`,
        `"${user.emailVerified ? 'Yes' : 'No'}"`,
        `"${user.phone || ''}"`,
        `"${user.phoneVerified ? 'Yes' : 'No'}"`,
        `"${user.role || 'user'}"`,
        `"${user.deactivated ? 'Suspended' : 'Active'}"`,
        `"${user.createdAt ? user.createdAt.toLocaleDateString() : 'N/A'}"`,
        `"${user.lastLogin ? user.lastLogin.toLocaleDateString() : 'Never'}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'Admin': return "badge bg-danger";
      case 'Professional': return "badge bg-info text-dark";
      case 'Organisation': return "badge bg-warning text-dark";
      default: return "badge bg-primary";
    }
  };

  const getStatusBadgeClass = (user) => {
    if (user.deactivated) return "badge bg-danger";
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    if (user.lastLogin && user.lastLogin >= sevenDaysAgo) return "badge bg-success";
    return "badge bg-secondary";
  };

  const getStatusText = (user) => {
    if (user.deactivated) return "Suspended";
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    if (user.lastLogin && user.lastLogin >= sevenDaysAgo) return "Active";
    return "Inactive";
  };

  // Evaluated Skills calculation based on real sub-collection data
  const evaluatedSkills = [
    {
      name: "Verilog HDL",
      score: Math.min(100, ((userDetailedData.enrolledCourses.length || 0) * 20) + ((userDetailedData.testResults.length || 0) * 10))
    },
    {
      name: "SystemVerilog",
      score: Math.min(100, ((userDetailedData.readModules.length || 0) * 15) + ((userDetailedData.projects.length || 0) * 15))
    },
    {
      name: "UVM Verification",
      score: Math.min(100, ((userDetailedData.enrolledCourses.length || 0) * 25) + ((userDetailedData.testResults.length || 0) * 12))
    },
    {
      name: "Digital Electronics & Logic Design",
      score: Math.min(100, ((userDetailedData.enrolledCourses.length || 0) * 30) + ((userDetailedData.projects.length || 0) * 20))
    },
    {
      name: "FPGA Synthesis & ASIC Architecture",
      score: Math.min(100, ((userDetailedData.readModules.length || 0) * 18) + ((userDetailedData.projects.length || 0) * 22))
    },
    {
      name: "VHDL Hardware Description",
      score: Math.min(100, ((userDetailedData.enrolledCourses.length || 0) * 15) + ((userDetailedData.testResults.length || 0) * 8))
    },
    {
      name: "Python Automation & Cocotb",
      score: Math.min(100, ((userDetailedData.readModules.length || 0) * 20) + ((userDetailedData.projects.length || 0) * 15))
    }
  ].map(s => {
    let level = "Beginner";
    let levelClass = styles.badgeBeginner;
    if (s.score >= 75) { level = "Expert"; levelClass = styles.badgeExpert; }
    else if (s.score >= 45) { level = "Advanced"; levelClass = styles.badgeAdvanced; }
    else if (s.score >= 20) { level = "Intermediate"; levelClass = styles.badgeIntermediate; }
    return { ...s, level, levelClass };
  });

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading users...</p>
      </div>
    );
  }

  return (
    <div className={styles.userManagement}>
      {/* Top Header */}
      <PageHeader
        icon={Users}
        title="User Management"
        subtitle="Manage user accounts, roles, permissions, and admin access control"
        badge={`${filteredUsers.length} Users`}
        actions={
          <button className={styles.exportBtn} onClick={exportUsers}>
            <Download size={16} className="me-2" />
            Export CSV
          </button>
        }
      />

      {/* Filter Toolbar */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, email, username, phone, city, state, designation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterGroup}>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Roles</option>
            <option value="Admin">Admin</option>
            <option value="Student">Student</option>
            <option value="Professional">Professional</option>
            <option value="Organisation">Organisation</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={filterDeletedStatus}
            onChange={(e) => setFilterDeletedStatus(e.target.value)}
            className={`${styles.filterSelect} ${filterDeletedStatus === 'deleted' ? styles.filterSelectDanger : ''}`}
          >
            <option value="active">✅ Active Accounts</option>
            <option value="deactivated">⚠️ Suspended Accounts</option>
            <option value="deleted">🗑️ Deleted Accounts</option>
            <option value="all">📋 All Accounts</option>
          </select>
        </div>
      </div>

      {/* Stats Summary Bar */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{users.length}</div>
          <div className={styles.statLabel}>Total Users</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{users.filter(u => u.role === 'Admin').length}</div>
          <div className={styles.statLabel}>Admins</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{users.filter(u => u.role === 'Student' || !u.role).length}</div>
          <div className={styles.statLabel}>Students</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{users.filter(u => u.role === 'Professional').length}</div>
          <div className={styles.statLabel}>Professionals</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {users.filter(u => {
              const now = new Date();
              const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              return u.lastLogin && u.lastLogin >= sevenDaysAgo && !u.deactivated;
            }).length}
          </div>
          <div className={styles.statLabel}>Active Users</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{users.filter(u => u.deactivated === true).length}</div>
          <div className={styles.statLabel}>Suspended</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{users.filter(u => u.deleted === true).length}</div>
          <div className={styles.statLabel}>Deleted</div>
        </div>
      </div>

      {/* Streamlined Main User Card Table with Email & Mobile Verification Badges */}
      <div className={styles.userTable}>
        <div className={styles.tableHeader}>
          <div className={styles.tableRow}>
            <div className={styles.colUsername}>User ID</div>
            <div className={styles.colName}>Full Name</div>
            <div className={styles.colContact}>Contact Info</div>
            <div className={styles.colRole}>Role & Status</div>
            <div className={styles.colActions}>Actions</div>
          </div>
        </div>

        <div className={styles.tableBody}>
          {filteredUsers.map((user) => (
            <div key={user.id} className={styles.tableRow}>
              {/* Col 1: Username / ID */}
              <div className={styles.colUsername}>
                <code className={styles.usernameCode}>
                  @{user.username || user.email?.split('@')[0]}
                </code>
              </div>

              {/* Col 2: Full Name */}
              <div className={styles.colName}>
                <div className={styles.userNameText}>
                  {user.name || user.email?.split('@')[0]}
                </div>
              </div>

              {/* Col 3: Contact Info with Verified Icons */}
              <div className={styles.colContact}>
                <div className={styles.contactInfo}>
                  <div className={styles.contactItem}>
                    <Mail size={12} className="me-1" />
                    <span>{user.email}</span>
                    {user.emailVerified ? (
                      <CheckCircle size={12} className="ms-1 text-success" title="Email Verified" />
                    ) : (
                      <span className="badge bg-secondary ms-1 py-0 px-1" style={{ fontSize: '0.65rem' }}>Unverified</span>
                    )}
                  </div>
                  {user.phone && (
                    <div className={styles.contactItem}>
                      <Phone size={12} className="me-1" />
                      <span>{user.phone}</span>
                      {user.phoneVerified ? (
                        <CheckCircle size={12} className="ms-1 text-success" title="Mobile Verified" />
                      ) : (
                        <span className="badge bg-secondary ms-1 py-0 px-1" style={{ fontSize: '0.65rem' }}>Unverified</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Col 4: Role & Status */}
              <div className={styles.colRole}>
                <div className={styles.roleSection}>
                  <select
                    value={user.role || 'Student'}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className={styles.roleSelect}
                  >
                    <option value="Student">Student</option>
                    <option value="Professional">Professional</option>
                    <option value="Admin">Admin</option>
                    <option value="Organisation">Organisation</option>
                  </select>
                  <div className={styles.accessInfo}>
                    <span className={getStatusBadgeClass(user)}>
                      {getStatusText(user)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Col 5: Quick Actions */}
              <div className={styles.colActions}>
                <div className={styles.actionButtons}>
                  <button
                    className={styles.actionBtn}
                    onClick={() => openUserModal(user)}
                    title="View & Manage User Profile"
                  >
                    <Eye size={16} />
                  </button>
                  {filterDeletedStatus !== 'deleted' && (
                    <>
                      <button
                        className={`${styles.actionBtn} ${user.deactivated ? styles.reactivateBtn : styles.suspendBtn}`}
                        onClick={() => handleSuspendUser(user.id, !user.deactivated)}
                        title={user.deactivated ? "Reactivate Account" : "Suspend Account"}
                      >
                        {user.deactivated ? <UserCheck size={16} /> : <UserX size={16} />}
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        onClick={() => {
                          setUserToDelete(user);
                          setShowDeleteModal(true);
                        }}
                        title="Delete User"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                  {filterDeletedStatus === 'deleted' && (
                    <button
                      className={`${styles.actionBtn} ${styles.restoreBtn}`}
                      onClick={() => {
                        setUserToRestore(user);
                        setShowRestoreModal(true);
                      }}
                      title="Restore User"
                    >
                      <UserCheck size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {filteredUsers.length === 0 && (
        <div className={styles.emptyState}>
          <Users size={48} className="mb-3 opacity-50" />
          <h5>No users found</h5>
          <p>Try adjusting your search query or role/status filters</p>
        </div>
      )}

      {/* ====================================================================
          REDESIGNED MULTI-TAB USER PROFILE MODAL (FOR ADMIN ACCESS CONTROL)
          ==================================================================== */}
      {showUserModal && selectedUser && (
        <div className={styles.modalOverlay} onClick={() => setShowUserModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            {/* Modal Hero Header */}
            <div className={styles.modalHeroHeader}>
              <div className={styles.modalHeroContent}>
                <div className={styles.modalAvatarWrapper}>
                  <img
                    src={selectedUser.profileImage || "/static_img/DP.jpg"}
                    alt={selectedUser.name || "User"}
                    onError={(e) => { e.target.src = "/static_img/DP.jpg"; }}
                  />
                </div>
                <div className={styles.modalHeroMain}>
                  <h3 className={styles.modalUserName}>
                    {selectedUser.name || selectedUser.username || selectedUser.email}
                  </h3>
                  <div className={styles.modalUserHandle}>@{selectedUser.username || 'user'}</div>
                  <div className={styles.modalHeroBadges}>
                    <span className={getRoleBadgeClass(selectedUser.role)}>
                      {selectedUser.role || 'Student'}
                    </span>
                    <span className={getStatusBadgeClass(selectedUser)}>
                      {getStatusText(selectedUser)}
                    </span>
                    {selectedUser.customBadge && (
                      <span className="badge bg-warning text-dark me-1">
                        <Award size={11} className="me-1" />
                        {selectedUser.customBadge}
                      </span>
                    )}
                    <span className={styles.heroTag}>
                      <Calendar size={12} className="me-1" />
                      Joined: {selectedUser.createdAt ? selectedUser.createdAt.toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
              <button
                className={styles.heroCloseBtn}
                onClick={() => setShowUserModal(false)}
                title="Close Profile Modal"
              >
                ×
              </button>
            </div>

            {/* Modal Tab Navigation Bar (Matched with UserSection Account Tab System) */}
            <div className={styles.profileTabNav}>
              <button
                className={`${styles.profileTabBtn} ${modalTab === 'overview' ? styles.profileTabBtnActive : ''}`}
                onClick={() => setModalTab('overview')}
              >
                <User size={16} /> Overview
              </button>
              <button
                className={`${styles.profileTabBtn} ${modalTab === 'points' ? styles.profileTabBtnActive : ''}`}
                onClick={() => setModalTab('points')}
              >
                <Award size={16} /> Silicon Points
                <span className={styles.tabBadgeCount}>{userDetailedData.pointsData?.totalPoints || 0}</span>
              </button>
              <button
                className={`${styles.profileTabBtn} ${modalTab === 'learning' ? styles.profileTabBtnActive : ''}`}
                onClick={() => setModalTab('learning')}
              >
                <BookOpen size={16} /> Learning & Practice
                <span className={styles.tabBadgeCount}>
                  {(userDetailedData.enrolledCourses?.length || 0) +
                    (userDetailedData.readModules?.length || 0) +
                    (userDetailedData.testResults?.length || 0) +
                    (userDetailedData.projects?.length || 0)}
                </span>
              </button>
              <button
                className={`${styles.profileTabBtn} ${modalTab === 'events' ? styles.profileTabBtnActive : ''}`}
                onClick={() => setModalTab('events')}
              >
                <Calendar size={16} /> Events
                <span className={styles.tabBadgeCount}>{userDetailedData.events?.length || 0}</span>
              </button>
              <button
                className={`${styles.profileTabBtn} ${modalTab === 'certificates' ? styles.profileTabBtnActive : ''}`}
                onClick={() => setModalTab('certificates')}
              >
                <FileText size={16} /> Certificates
                <span className={styles.tabBadgeCount}>{userDetailedData.certificates?.length || 0}</span>
              </button>
              <button
                className={`${styles.profileTabBtn} ${modalTab === 'skills' ? styles.profileTabBtnActive : ''}`}
                onClick={() => setModalTab('skills')}
              >
                <CheckCircle size={16} /> Skills
              </button>
              <button
                className={`${styles.profileTabBtn} ${modalTab === 'admin_settings' ? styles.profileTabBtnActive : ''}`}
                onClick={() => setModalTab('admin_settings')}
              >
                <Sliders size={16} /> Admin Settings
              </button>
            </div>

            {/* Modal Body with Active Tab Content */}
            <div className={styles.modalTabBody}>
              {isDetailLoading ? (
                <div className={styles.tabLoading}>
                  <div className="spinner-border text-primary mb-2" role="status" />
                  <p>Loading complete user data & sub-collections...</p>
                </div>
              ) : (
                <>
                  {/* TAB 1: OVERVIEW */}
                  {modalTab === 'overview' && (
                    <div>
                      {/* User Dashboard Stats Cards — Exact 10 Cards Grid (Matching User Dashboard Order) */}
                      <div className={styles.dashboardStatsGrid}>
                        {/* 1. Courses */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#ebf8ff", color: "#3182ce" }}>
                            <BookOpen size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Courses</p>
                            <p className={styles.dashStatValue}>{userDetailedData.enrolledCourses?.length || 0}</p>
                          </div>
                        </div>

                        {/* 2. Read Modules */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#f3e8ff", color: "#8b5cf6" }}>
                            <Layers size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Read Modules</p>
                            <p className={styles.dashStatValue}>{userDetailedData.readModules?.length || 0}</p>
                          </div>
                        </div>

                        {/* 3. Tests */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#fef3c7", color: "#d97706" }}>
                            <Briefcase size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Tests</p>
                            <p className={styles.dashStatValue}>{userDetailedData.testResults?.length || 0}</p>
                          </div>
                        </div>

                        {/* 4. Projects */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#d1fae5", color: "#10b981" }}>
                            <GitBranch size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Projects</p>
                            <p className={styles.dashStatValue}>{userDetailedData.projects?.length || 0}</p>
                          </div>
                        </div>

                        {/* 5. Events */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}>
                            <Calendar size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Events</p>
                            <p className={styles.dashStatValue}>{userDetailedData.events?.length || 0}</p>
                          </div>
                        </div>

                        {/* 6. Code Practice */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#ecfdf5", color: "#059669" }}>
                            <Terminal size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Code Practice</p>
                            <p className={styles.dashStatValue} style={{ color: "#059669" }}>{userDetailedData.codePracticeCount || 0}</p>
                          </div>
                        </div>

                        {/* 7. Silicon Points */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#fef3c7", color: "#d97706" }}>
                            <Award size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Silicon Points</p>
                            <p className={styles.dashStatValue} style={{ color: "#d97706" }}>{userDetailedData.pointsData?.totalPoints || 0}</p>
                          </div>
                        </div>

                        {/* 8. Certificates */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#fee2e2", color: "#ef4444" }}>
                            <FileText size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Certificates</p>
                            <p className={styles.dashStatValue}>{userDetailedData.certificates?.length || 0}</p>
                          </div>
                        </div>

                        {/* 9. Blogs Posted */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#fce7f3", color: "#db2777" }}>
                            <Rss size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Blogs Posted</p>
                            <p className={styles.dashStatValue}>{userDetailedData.blogsCount || 0}</p>
                          </div>
                        </div>

                        {/* 10. Notes Added */}
                        <div className={styles.dashStatCard}>
                          <div className={styles.dashStatIconBox} style={{ backgroundColor: "#f1f5f9", color: "#475569" }}>
                            <Edit3 size={20} />
                          </div>
                          <div className={styles.dashStatContent}>
                            <p className={styles.dashStatLabel}>Notes Added</p>
                            <p className={styles.dashStatValue}>{userDetailedData.notesCount || 0}</p>
                          </div>
                        </div>
                      </div>

                      {/* Personal Info Card with Verified Badges */}
                      <div className={styles.sectionCard}>
                        <h5 className={styles.sectionTitle}>
                          <User size={18} className="me-2 text-primary" /> Personal Information
                        </h5>
                        <div className={styles.detailsGrid}>
                          <div className={styles.detailItem}>
                            <strong>Email Address</strong>
                            <div className="d-flex align-items-center justify-content-between">
                              <span>{selectedUser.email}</span>
                              {selectedUser.emailVerified ? (
                                <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>
                                  <CheckCircle size={11} className="me-1" /> Verified
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark" style={{ fontSize: '0.75rem' }}>
                                  Unverified
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Phone Number</strong>
                            <div className="d-flex align-items-center justify-content-between">
                              <span>{selectedUser.phone || 'N/A'}</span>
                              {selectedUser.phone ? (
                                selectedUser.phoneVerified ? (
                                  <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>
                                    <CheckCircle size={11} className="me-1" /> Verified
                                  </span>
                                ) : (
                                  <span className="badge bg-warning text-dark" style={{ fontSize: '0.75rem' }}>
                                    Unverified
                                  </span>
                                )
                              ) : null}
                            </div>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>City</strong>
                            <span>{selectedUser.city || 'N/A'}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>State</strong>
                            <span>{selectedUser.state || 'N/A'}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Address / Location</strong>
                            <span>{selectedUser.location || selectedUser.address || 'N/A'}</span>
                          </div>
                          <div className={styles.detailItem} style={{ gridColumn: '1 / -1' }}>
                            <strong>Bio</strong>
                            <span>{selectedUser.bio || 'No bio provided'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Professional Info Card */}
                      <div className={styles.sectionCard}>
                        <h5 className={styles.sectionTitle}>
                          <Briefcase size={18} className="me-2 text-primary" /> Academic & Professional Info
                        </h5>
                        <div className={styles.detailsGrid}>
                          <div className={styles.detailItem}>
                            <strong>Account Role</strong>
                            <span>{selectedUser.role || 'Student'}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Designation / Stream</strong>
                            <span>{selectedUser.designation || selectedUser.stream || 'N/A'}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Company / College</strong>
                            <span>{selectedUser.department || selectedUser.company || selectedUser.institute || 'N/A'}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Education Level</strong>
                            <span>{selectedUser.education || selectedUser.qualification || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Social & Web Links */}
                      {(selectedUser.linkedin || selectedUser.github || selectedUser.website) && (
                        <div className={styles.sectionCard}>
                          <h5 className={styles.sectionTitle}>
                            <Globe size={18} className="me-2 text-primary" /> Social & Online Links
                          </h5>
                          <div className={styles.detailsGrid}>
                            {selectedUser.linkedin && (
                              <div className={styles.detailItem}>
                                <strong>LinkedIn Profile</strong>
                                <a href={selectedUser.linkedin} target="_blank" rel="noreferrer">
                                  {selectedUser.linkedin} <ExternalLink size={12} />
                                </a>
                              </div>
                            )}
                            {selectedUser.github && (
                              <div className={styles.detailItem}>
                                <strong>GitHub Profile</strong>
                                <a href={selectedUser.github} target="_blank" rel="noreferrer">
                                  {selectedUser.github} <ExternalLink size={12} />
                                </a>
                              </div>
                            )}
                            {selectedUser.website && (
                              <div className={styles.detailItem}>
                                <strong>Personal Website</strong>
                                <a href={selectedUser.website} target="_blank" rel="noreferrer">
                                  {selectedUser.website} <ExternalLink size={12} />
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB 2: SILICON POINTS */}
                  {modalTab === 'points' && (
                    <div>
                      <div className={styles.pointsBanner}>
                        <div>
                          <div className={styles.rankTitle}>
                            Rank: {userDetailedData.pointsData?.rank || "Silicon Initiate"}
                          </div>
                          <p className="mb-0 opacity-75">User Loyalty & Engagement Honor Rating</p>
                        </div>
                        <div className={styles.pointsTotal}>
                          {userDetailedData.pointsData?.totalPoints || 0} PTS
                        </div>
                      </div>

                      <div className={styles.sectionCard}>
                        <h5 className={styles.sectionTitle}>
                          <Award size={18} className="me-2 text-primary" /> Points Breakdown & Activity Credits
                        </h5>
                        <div className={styles.activityGrid}>
                          <div className={styles.activityCard}>
                            <div className={styles.activityNumber}>
                              {userDetailedData.pointsData?.breakdown?.courses?.points ?? 0}
                            </div>
                            <div className={styles.activityLabel}>
                              Course Progress ({userDetailedData.pointsData?.breakdown?.courses?.count || 0})
                            </div>
                          </div>
                          <div className={styles.activityCard}>
                            <div className={styles.activityNumber}>
                              {userDetailedData.pointsData?.breakdown?.modules?.points ?? 0}
                            </div>
                            <div className={styles.activityLabel}>
                              Read Modules ({userDetailedData.pointsData?.breakdown?.modules?.count || 0})
                            </div>
                          </div>
                          <div className={styles.activityCard}>
                            <div className={styles.activityNumber}>
                              {userDetailedData.pointsData?.breakdown?.tests?.points ?? 0}
                            </div>
                            <div className={styles.activityLabel}>
                              Evaluation Tests ({userDetailedData.pointsData?.breakdown?.tests?.count || 0})
                            </div>
                          </div>
                          <div className={styles.activityCard}>
                            <div className={styles.activityNumber}>
                              {userDetailedData.pointsData?.breakdown?.projects?.points ?? 0}
                            </div>
                            <div className={styles.activityLabel}>
                              Lab Projects ({userDetailedData.pointsData?.breakdown?.projects?.count || 0})
                            </div>
                          </div>
                          <div className={styles.activityCard}>
                            <div className={styles.activityNumber}>
                              {userDetailedData.pointsData?.breakdown?.events?.points ?? 0}
                            </div>
                            <div className={styles.activityLabel}>
                              Attended Events ({userDetailedData.pointsData?.breakdown?.events?.count || 0})
                            </div>
                          </div>
                          <div className={styles.activityCard}>
                            <div className={styles.activityNumber}>
                              {userDetailedData.pointsData?.breakdown?.codePractice?.points ?? 0}
                            </div>
                            <div className={styles.activityLabel}>
                              Code Practice ({userDetailedData.pointsData?.breakdown?.codePractice?.count || 0})
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TAB 3: LEARNING & PRACTICE */}
                  {modalTab === 'learning' && (
                    <div>
                      {/* Enrolled Courses */}
                      <div className={styles.sectionCard}>
                        <h5 className={styles.sectionTitle}>
                          <BookOpen size={18} className="me-2 text-primary" /> Enrolled Courses ({userDetailedData.enrolledCourses?.length || 0})
                        </h5>
                        {userDetailedData.enrolledCourses?.length > 0 ? (
                          userDetailedData.enrolledCourses.map(course => (
                            <div key={course.id || course.enrollmentId} className={styles.courseCard}>
                              <div className="flex-grow-1 me-3">
                                <h6 className="mb-1 fw-bold">{course.title}</h6>
                                <p className="mb-0 text-muted small">
                                  Category: {course.category} | Status: {course.status}
                                  {course.completedLessons ? ` | Completed Lessons: ${course.completedLessons}` : ''}
                                </p>
                              </div>
                              <div className="text-end" style={{ minWidth: '140px' }}>
                                <span className={`badge ${course.progress >= 100 ? 'bg-success' : 'bg-primary'}`}>
                                  {course.progress || 0}% Complete
                                </span>
                                <div className={styles.progressTrack} style={{ width: '130px', marginTop: '6px' }}>
                                  <div className={styles.progressFill} style={{ width: `${Math.min(100, course.progress || 0)}%` }} />
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted mb-0">No courses enrolled yet.</p>
                        )}
                      </div>

                      {/* Read Modules Progress Section */}
                      <div className={styles.sectionCard}>
                        <h5 className={styles.sectionTitle}>
                          <BookOpen size={18} className="me-2 text-primary" /> Read Modules Progress ({userDetailedData.readModules?.length || 0})
                        </h5>
                        {userDetailedData.readModules?.length > 0 ? (
                          userDetailedData.readModules.map(module => (
                            <div key={module.id || module.progressId} className={styles.courseCard}>
                              <div className="flex-grow-1 me-3">
                                <h6 className="mb-1 fw-bold">{module.title}</h6>
                                <p className="mb-0 text-muted small">
                                  Completed Topics: {module.completedTopics || 0}
                                  {module.lastUpdated ? ` | Last Read: ${module.lastUpdated.toLocaleDateString()}` : ''}
                                </p>
                              </div>
                              <div className="text-end" style={{ minWidth: '140px' }}>
                                <span className={`badge ${module.percentage >= 100 ? 'bg-success' : 'bg-primary'}`}>
                                  {module.percentage}% Read
                                </span>
                                <div className={styles.progressTrack} style={{ width: '130px', marginTop: '6px' }}>
                                  <div className={styles.progressFill} style={{ width: `${Math.min(100, module.percentage || 0)}%` }} />
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted mb-0">No read modules completed yet.</p>
                        )}
                      </div>

                      {/* Evaluation Tests Taken */}
                      <div className={styles.sectionCard}>
                        <h5 className={styles.sectionTitle}>
                          <BarChart size={18} className="me-2 text-primary" /> Test Evaluation History ({userDetailedData.testResults?.length || 0})
                        </h5>
                        {userDetailedData.testResults?.length > 0 ? (
                          userDetailedData.testResults.map((test, index) => (
                            <div key={test.id || index} className={styles.courseCard}>
                              <div>
                                <h6 className="mb-1 fw-bold">{test.testTitle}</h6>
                                <p className="mb-0 text-muted small">
                                  Score: {test.score}% | {test.passed ? "Passed ✅" : "Attempted"}
                                  {test.takenAt ? ` | Date: ${test.takenAt.toLocaleDateString()}` : ''}
                                </p>
                              </div>
                              <span className={`badge ${test.passed ? 'bg-success' : 'bg-secondary'}`}>
                                {test.passed ? 'Passed' : 'Completed'}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted mb-0">No test evaluation results found.</p>
                        )}
                      </div>

                      {/* Lab Projects Submitted */}
                      <div className={styles.sectionCard}>
                        <h5 className={styles.sectionTitle}>
                          <Briefcase size={18} className="me-2 text-primary" /> Lab Projects ({userDetailedData.projects?.length || 0})
                        </h5>
                        {userDetailedData.projects?.length > 0 ? (
                          userDetailedData.projects.map(proj => (
                            <div key={proj.id} className={styles.courseCard}>
                              <div>
                                <h6 className="mb-1 fw-bold">{proj.title}</h6>
                                <p className="mb-0 text-muted small">
                                  Status: {proj.status}
                                  {proj.submittedAt ? ` | Submitted: ${proj.submittedAt.toLocaleDateString()}` : ''}
                                </p>
                              </div>
                              <span className="badge bg-info text-dark">{proj.status}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted mb-0">No lab project submissions found.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* TAB 4: EVENTS */}
                  {modalTab === 'events' && (
                    <div className={styles.sectionCard}>
                      <h5 className={styles.sectionTitle}>
                        <Calendar size={18} className="me-2 text-primary" /> Event Registrations ({userDetailedData.events?.length || 0})
                      </h5>
                      {userDetailedData.events?.length > 0 ? (
                        userDetailedData.events.map(ev => (
                          <div key={ev.id} className={styles.eventCard}>
                            <div>
                              <h6 className="mb-1 fw-bold">{ev.eventTitle}</h6>
                              <p className="mb-0 text-muted small">
                                Ticket Code: {ev.ticketCode} | Category: {ev.eventCategory}
                                {ev.registeredAt ? ` | Registered: ${ev.registeredAt.toLocaleDateString()}` : ''}
                              </p>
                            </div>
                            <span className={`badge ${ev.attended ? 'bg-success' : 'bg-info text-dark'}`}>
                              {ev.attended ? 'Attended ✅' : (ev.status || 'Registered')}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted mb-0">No registered events found for this user.</p>
                      )}
                    </div>
                  )}

                  {/* TAB 5: CERTIFICATES */}
                  {modalTab === 'certificates' && (
                    <div className={styles.sectionCard}>
                      <h5 className={styles.sectionTitle}>
                        <FileText size={18} className="me-2 text-primary" /> User Earned Certificates ({userDetailedData.certificates?.length || 0})
                      </h5>
                      {userDetailedData.certificates?.length > 0 ? (
                        userDetailedData.certificates.map(cert => (
                          <div key={cert.id} className={styles.certCard}>
                            <div>
                              <h6 className="mb-1 fw-bold">{cert.title}</h6>
                              <p className="mb-0 text-muted small">Certificate ID: {cert.certificateId}</p>
                            </div>
                            <span className="badge bg-success">Verified ✅</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted mb-0">No certificates generated yet.</p>
                      )}
                    </div>
                  )}

                  {/* TAB 6: SKILLS */}
                  {modalTab === 'skills' && (
                    <div className={styles.sectionCard}>
                      <h5 className={styles.sectionTitle}>
                        <CheckCircle size={18} className="me-2 text-primary" /> Evaluated Skill Matrix
                      </h5>
                      {evaluatedSkills.map(skill => (
                        <div key={skill.name} className={styles.skillCard}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fw-bold">{skill.name}</span>
                            <span className={`badge ${skill.levelClass}`}>{skill.level} ({skill.score}%)</span>
                          </div>
                          <div className={styles.progressTrack}>
                            <div className={styles.progressFill} style={{ width: `${skill.score}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* TAB 7: ADMIN SETTINGS (ADMIN CONTROL HUB & VERIFICATION DISPATCH) */}
                  {modalTab === 'admin_settings' && (
                    <div>
                      {adminMsg.text && (
                        <div className={adminMsg.type === 'success' ? styles.alertBoxSuccess : styles.alertBoxDanger}>
                          {adminMsg.text}
                        </div>
                      )}

                      <div className={styles.adminControlGrid}>
                        {/* Section 1: Role & Access Control */}
                        <div className={styles.sectionCard}>
                          <h6 className={styles.sectionTitle}>
                            <Shield size={16} className="me-2 text-primary" /> Role & Access Control
                          </h6>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>User Role</label>
                            <select
                              value={adminFormState.role}
                              onChange={(e) => setAdminFormState({ ...adminFormState, role: e.target.value })}
                              className={styles.formSelect}
                            >
                              <option value="Student">Student</option>
                              <option value="Professional">Professional</option>
                              <option value="Admin">Admin</option>
                              <option value="Organisation">Organisation</option>
                            </select>
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Account Suspension Status</label>
                            <div className="form-check form-switch mt-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="suspendSwitchModal"
                                checked={adminFormState.deactivated}
                                onChange={(e) => setAdminFormState({ ...adminFormState, deactivated: e.target.checked })}
                              />
                              <label className="form-check-label fw-bold" htmlFor="suspendSwitchModal">
                                {adminFormState.deactivated ? "⚠️ Account Suspended" : "✅ Account Active"}
                              </label>
                            </div>
                          </div>

                          {adminFormState.deactivated && (
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>Suspension Reason</label>
                              <input
                                type="text"
                                className={styles.formInput}
                                placeholder="Enter reason for account suspension..."
                                value={adminFormState.deactivationReason}
                                onChange={(e) => setAdminFormState({ ...adminFormState, deactivationReason: e.target.value })}
                              />
                            </div>
                          )}
                        </div>

                        {/* Section 2: Honor Badges & Bonus Points Grant */}
                        <div className={styles.sectionCard}>
                          <h6 className={styles.sectionTitle}>
                            <Award size={16} className="me-2 text-primary" /> Badges & Points Grant
                          </h6>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Custom Honor Badge Title</label>
                            <input
                              type="text"
                              className={styles.formInput}
                              placeholder="e.g. Silicon Scholar, Top Verilog Coder"
                              value={adminFormState.customBadge}
                              onChange={(e) => setAdminFormState({ ...adminFormState, customBadge: e.target.value })}
                            />
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Grant Bonus Silicon Points</label>
                            <div className="d-flex gap-2">
                              <input
                                type="number"
                                className={styles.formInput}
                                placeholder="Points (+100)"
                                value={adminFormState.bonusPoints}
                                onChange={(e) => setAdminFormState({ ...adminFormState, bonusPoints: e.target.value })}
                              />
                              <input
                                type="text"
                                className={styles.formInput}
                                placeholder="Reason"
                                value={adminFormState.bonusReason}
                                onChange={(e) => setAdminFormState({ ...adminFormState, bonusReason: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Section 3: Password & Security Actions */}
                        <div className={styles.sectionCard}>
                          <h6 className={styles.sectionTitle}>
                            <Key size={16} className="me-2 text-primary" /> Security & Password Control
                          </h6>
                          <p className="text-muted small mb-2">
                            Trigger password reset email to user's registered address ({selectedUser.email}).
                          </p>
                          <button
                            className={styles.btnWarning}
                            onClick={handleSendPasswordResetModal}
                            disabled={isSendingReset}
                          >
                            <Send size={14} /> {isSendingReset ? "Sending Reset Email..." : "Send Password Reset Email"}
                          </button>

                          <div className="form-check form-switch mt-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="forcePasswordResetCheck"
                              checked={adminFormState.forcePasswordReset}
                              onChange={(e) => setAdminFormState({ ...adminFormState, forcePasswordReset: e.target.checked })}
                            />
                            <label className="form-check-label small fw-bold" htmlFor="forcePasswordResetCheck">
                              Force Password Reset on Next Login
                            </label>
                          </div>
                        </div>

                        {/* Section 4: Contact Verification Request & Override Hub */}
                        <div className={styles.sectionCard}>
                          <h6 className={styles.sectionTitle}>
                            <CheckCircle size={16} className="me-2 text-primary" /> Verification Requests & Override
                          </h6>
                          <div className="d-flex flex-column gap-3">
                            {/* Email Verification Action */}
                            <div className="p-2 border rounded">
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="fw-bold small"><Mail size={13} className="me-1 text-primary" /> Email</span>
                                {selectedUser.emailVerified ? (
                                  <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>Verified ✅</span>
                                ) : (
                                  <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>Unverified ⚠️</span>
                                )}
                              </div>
                              <button
                                className="btn btn-sm btn-outline-primary w-100 mb-2 py-1"
                                onClick={handleSendEmailVerificationReq}
                                disabled={isSendingEmailVerify}
                                style={{ fontSize: '0.8rem' }}
                              >
                                <Send size={12} className="me-1" />
                                {isSendingEmailVerify ? "Sending Request..." : "Send Email Verification Request"}
                              </button>
                              <div className="form-check form-switch m-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="adminEmailVerifySwitch"
                                  checked={adminFormState.emailVerified}
                                  onChange={(e) => setAdminFormState({ ...adminFormState, emailVerified: e.target.checked })}
                                />
                                <label className="form-check-label small fw-bold" htmlFor="adminEmailVerifySwitch" style={{ fontSize: '0.78rem' }}>
                                  Admin Override: Email Verified
                                </label>
                              </div>
                            </div>

                            {/* Mobile Verification Action */}
                            <div className="p-2 border rounded">
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="fw-bold small"><Phone size={13} className="me-1 text-primary" /> Mobile / SMS</span>
                                {selectedUser.phoneVerified ? (
                                  <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>Verified ✅</span>
                                ) : (
                                  <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>Unverified ⚠️</span>
                                )}
                              </div>
                              <button
                                className="btn btn-sm btn-outline-info w-100 mb-2 py-1"
                                onClick={handleSendMobileVerificationReq}
                                disabled={isSendingMobileVerify}
                                style={{ fontSize: '0.8rem' }}
                              >
                                <Send size={12} className="me-1" />
                                {isSendingMobileVerify ? "Dispatching SMS..." : "Send Mobile / SMS Verification Request"}
                              </button>
                              <div className="form-check form-switch m-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="adminPhoneVerifySwitch"
                                  checked={adminFormState.phoneVerified}
                                  onChange={(e) => setAdminFormState({ ...adminFormState, phoneVerified: e.target.checked })}
                                />
                                <label className="form-check-label small fw-bold" htmlFor="adminPhoneVerifySwitch" style={{ fontSize: '0.78rem' }}>
                                  Admin Override: Mobile Verified
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Section 5: Private Admin Notes */}
                        <div className={styles.sectionCard}>
                          <h6 className={styles.sectionTitle}>
                            <FileText size={16} className="me-2 text-primary" /> Internal Admin Notes
                          </h6>
                          <div className={styles.formGroup}>
                            <textarea
                              className={styles.formTextarea}
                              placeholder="Add private admin comments or notes regarding this user..."
                              value={adminFormState.adminNotes}
                              onChange={(e) => setAdminFormState({ ...adminFormState, adminNotes: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Section 6: System Audit Metadata & UID */}
                      <div className={styles.sectionCard}>
                        <h6 className={styles.sectionTitle}>
                          <Database size={16} className="me-2 text-primary" /> System Metadata & Audit Info
                        </h6>
                        <div className={styles.detailsGrid}>
                          <div className={styles.detailItem}>
                            <strong>User UID</strong>
                            <div className="d-flex align-items-center">
                              <code className="me-2">{selectedUser.id}</code>
                              <button
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                onClick={() => handleCopyUid(selectedUser.id)}
                              >
                                {copiedUid ? <Check size={12} /> : <Copy size={12} />}
                                {copiedUid ? " Copied!" : " Copy"}
                              </button>
                            </div>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Registration Date</strong>
                            <span>{selectedUser.createdAt ? selectedUser.createdAt.toLocaleString() : 'N/A'}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Last Sign In</strong>
                            <span>{selectedUser.lastLogin ? selectedUser.lastLogin.toLocaleString() : 'Never'}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Email Verified</strong>
                            <span>{selectedUser.emailVerified ? "Yes ✅" : "No ⚠️"}</span>
                          </div>
                        </div>

                        {userDetailedData.auditLogs?.length > 0 && (
                          <div className="mt-3">
                            <h6 className="small fw-bold text-muted mb-2">Recent Admin Action History</h6>
                            <div className={styles.auditLogList}>
                              {userDetailedData.auditLogs.map(log => (
                                <div key={log.id} className={styles.auditLogItem}>
                                  <strong>{log.action}</strong>
                                  <div className="text-muted">
                                    Performed at: {log.performedAt?.toDate ? log.performedAt.toDate().toLocaleString() : 'N/A'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Save Button for Admin Control Settings */}
                      <div className="d-flex justify-content-end mt-3">
                        <button
                          className={styles.btnPrimary}
                          onClick={handleSaveAdminSettings}
                          disabled={isSavingAdmin}
                        >
                          <Save size={16} /> {isSavingAdmin ? "Saving Admin Settings..." : "Save Admin Settings"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.modal} style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeroHeader} style={{ padding: '1rem 1.5rem' }}>
              <h5 className="m-0 text-white font-weight-bold">Confirm Account Deactivation</h5>
              <button className={styles.heroCloseBtn} onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className={styles.modalTabBody}>
              <p>Are you sure you want to deactivate user <strong>{userToDelete.name || userToDelete.email}</strong>?</p>
              <p className="text-danger small">This action will soft-delete the user and remove them from active directories.</p>
              <div className={styles.modalActions}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger btn-sm" onClick={handleDeleteUser}>Deactivate User</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && userToRestore && (
        <div className={styles.modalOverlay} onClick={() => setShowRestoreModal(false)}>
          <div className={styles.modal} style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeroHeader} style={{ padding: '1rem 1.5rem' }}>
              <h5 className="m-0 text-white font-weight-bold">Confirm Account Restoration</h5>
              <button className={styles.heroCloseBtn} onClick={() => setShowRestoreModal(false)}>×</button>
            </div>
            <div className={styles.modalTabBody}>
              <p>Are you sure you want to restore user <strong>{userToRestore.name || userToRestore.email}</strong>?</p>
              <p className="text-success small">This will reactivate the account and permit login access again.</p>
              <div className={styles.modalActions}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowRestoreModal(false)}>Cancel</button>
                <button className="btn btn-success btn-sm" onClick={handleRestoreUser}>Restore Account</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
